import React from 'react';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router';
import { Box, Divider, Paper, Typography, useTheme, Skeleton } from '@mui/material';
import { AxiosResponse } from 'axios';
import { useRouteScenario } from 'src/hooks/useRouteScenario';
import { makeRouteScenario } from 'src/utils/route';
import { IOrderReadResourceV18, OrderPaymentType, OrderScenario } from 'components/order/model/Order';
import { orderApi } from 'components/order/orderApi';
import { PayError } from 'components/pay/errors/PayError';
import { PayItemsHeader } from 'components/pay/order-item/PayItemsHeader';
import { getIdentityLabel } from 'components/settings/model/Settings';
import { SummaryGuestConversionSection } from 'components/summary/guestConversion/SummaryGuestConversionSection';
import { SummarySendReceiptSection } from 'components/summary/SummaryResendReceiptSection';
import { ApplicationState } from 'store/store';
import { PaySummaryItems } from 'components/pay/summary/PaySummaryItems';
import { PaySummaryPayments } from 'components/pay/summary/PaySummaryPayments';
import { PaySummaryReturn } from 'components/pay/summary/PaySummaryReturn';
import { CheckCircle } from 'ui/CheckCircle';
import { setOnCloseRedirectUrl } from 'components/user/localAuth';
import { LoadingTypography } from 'lib/LoadingTypography';
import { isDefined } from 'lib/typeInference';
import { useAuth } from 'lib/useAuth';
import { useLocalHistory } from 'lib/useLocalHistory';
import { BaseRouteParams, ROUTES } from './routes';
import { clearPaySessionStorage } from 'components/pay/model/helpers';
import { REDUCED_CONTENT_MAX_WIDTH } from 'config/constants';
import { FlatAppbar } from 'ui/FlatAppbar';
import { FlatToolbar } from 'ui/FlatToolbar';
import { useDeliverySummary } from 'components/delivery/hooks/useDeliverySummary';
import { ButtonV2 } from 'ui/ButtonV2';
import { DeliverySuccessBox } from 'components/delivery/success/DeliverySuccessBox';
import { DeliveryPinBox } from 'components/delivery/success/DeliveryPinBox';
import { ReviewPrompt } from 'components/order/ReviewPrompt';
import { useReviews } from 'components/order/hooks/useReviews';
import { EActionType } from '../components/activity/enums';

const PREFIX = 'PaySummaryPage';

const classes = {
    img: `${PREFIX}img`,
    title: `${PREFIX}title`,
    text: `${PREFIX}text`
};

const StyledBox = styled(Box)(({ theme }) => ({
    height: '100%',
    width: '100%',
    maxWidth: REDUCED_CONTENT_MAX_WIDTH,
    overflow: 'scroll',
    [`& .${classes.img}`]: {
        margin: theme.spacing(2, 0),
        color: theme.palette.primary.main
    },

    [`& .${classes.title}`]: {
        fontSize: '24px',
        paddingTop: theme.spacing(1),
        fontWeight: 500,
        color: theme.palette.text.primary
    },

    [`& .${classes.text}`]: {
        color: theme.palette.text.primary
    }
}));

enum ErrorType {
    GENERIC,
    NOT_FOUND,
    NONE
}

export const PaySummaryPage: React.FC = () => {
    const { t } = useTranslation();
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(ErrorType.NONE);
    const [errorMessage, setErrorMessage] = React.useState('');
    const [order, setOrder] = React.useState<null | IOrderReadResourceV18>(null);
    const { locationId, orderId } = useParams<{ locationId: string; orderId: string }>();
    const { review, loading: reviewLoading, reviewsEnabled } = useReviews(order?.id);
    const { push } = useLocalHistory();

    const [scenario] = useRouteScenario();
    const { isGuest, user } = useAuth();
    const theme = useTheme();
    const { settings } = useSelector((state: ApplicationState) => state.settings);
    const isPhoneAuth = getIdentityLabel(settings?.emailAuthEnabled, settings?.phoneAuthEnabled) === 'phone';
    const isOrderEmailNotificationsEnabled = React.useMemo(
        () =>
            settings &&
            settings.actionNotifications &&
            (settings.actionNotifications[EActionType.ORDER_USER_BILLED]?.email ||
                settings.actionNotifications[EActionType.ORDER_COMPLETED]?.email),

        [settings]
    );

    const paid = React.useMemo(() => {
        if (!!order && Array.isArray(order?.payments)) {
            return order.payments.reduce<{ giftCard: number; otherPayments: number }>(
                (acc, orderPayment) => {
                    if (orderPayment.amount) {
                        if (orderPayment.userId !== user?._id) {
                            return acc;
                        }
                        if (orderPayment.type !== OrderPaymentType.GIFT_CARD) {
                            acc.otherPayments += orderPayment.amount;
                        } else {
                            acc.giftCard += orderPayment.amount;
                        }
                    }
                    return acc;
                },
                { giftCard: 0, otherPayments: 0 }
            );
        }
        return { giftCard: 0, otherPayments: 0 };
    }, [order, user?._id]);

    const { merchantId } = useParams<BaseRouteParams>();
    const { pathname, search } = useLocation();
    const fullPath = React.useMemo(() => pathname + search, [pathname, search]);
    React.useEffect(() => {
        if (merchantId) {
            setOnCloseRedirectUrl(fullPath, merchantId);
        }
    }, [fullPath, merchantId]);

    const fetchOrder = React.useCallback(() => {
        async function getOrderStatus() {
            if (!orderId) {
                return;
            }
            try {
                const orderResult = await orderApi.getOrder(orderId);
                setOrder(orderResult);
                setLoading(false);
                clearPaySessionStorage(merchantId, locationId, orderId);
            } catch (orderStatusError) {
                const err = orderStatusError as AxiosResponse<{ message: string }>;
                if (isDefined(err.data && err.status === 404)) {
                    setError(ErrorType.NOT_FOUND);
                    setErrorMessage('Order not found');
                } else {
                    setError(ErrorType.GENERIC);
                    setErrorMessage(err.data.message);
                }
                setLoading(false);
            }
        }
        if (!!user) {
            getOrderStatus();
        }
    }, [locationId, merchantId, orderId, user]);

    const { showDelivery, showPin, trackMyOrderLink, due, statusString, updatedAt, pin, deliveryStage } =
        useDeliverySummary(order, fetchOrder);

    React.useEffect(() => {
        fetchOrder();
    }, [fetchOrder]);

    const title = React.useMemo(() => {
        if (scenario === OrderScenario.DELIVERY) {
            return t('ORDER_TITLE', { orderNumber: order?.id?.toString() });
        }
        return undefined;
    }, [order?.id, scenario, t]);
    const toolbarDescription = React.useMemo(() => {
        if (order?.delivery?.dropoffAddress) {
            return order?.delivery?.dropoffAddress?.address?.line1;
        }
        return undefined;
    }, [order?.delivery?.dropoffAddress]);

    React.useEffect(() => {
        if (!!order && order.scenario !== (scenario as OrderScenario) && orderId) {
            push(ROUTES.LEGACY.SUMMARY, { scenario: makeRouteScenario(order.scenario), orderId });
        }
    }, [push, scenario, order, orderId]);

    if (loading) {
        return (
            <StyledBox marginX="auto" className="hidden-scroll">
                <Paper style={{ minHeight: '100%' }}>
                    <FlatAppbar variant="small">
                        <FlatToolbar loading checkId="" disableBack disableEdit />
                    </FlatAppbar>
                    <Box display="flex" alignItems="center" flexDirection="column">
                        <LoadingTypography className={classes.title} loading width={150} />
                        <LoadingTypography className={classes.text} loading width={100} />
                        <div className={classes.img}>
                            <Skeleton width={83} height={83} variant="circular" />
                        </div>
                        <Divider />
                    </Box>
                    <Box px={2}>
                        <Divider />
                        <PayItemsHeader paid loading />
                    </Box>
                    <PaySummaryItems loading />
                </Paper>
            </StyledBox>
        );
    }

    return (
        <StyledBox marginX="auto" className="hidden-scroll">
            <Paper style={{ minHeight: '100%', borderRadius: 0 }}>
                <FlatAppbar variant="small">
                    <FlatToolbar
                        loading={loading}
                        table={order?.deliveryLocation}
                        checkId={order?.checkId}
                        disableBack
                        disableEdit
                        title={title}
                        description={toolbarDescription}
                    />
                </FlatAppbar>
                {!!order && scenario === OrderScenario.TABLE && (
                    <Box display="flex" alignItems="center" flexDirection="column">
                        <Typography className={classes.title}>
                            {t('PAT_QUICKPAY_PAYMENT_SUCCESSFUL_TITLE')}
                        </Typography>
                        <Typography className={classes.text}>
                            {order?.isOpen
                                ? t('PAT_QUICKPAY_PAYMENT_SUCCESSFUL_CONTENT_SPLIT')
                                : t('PAT_QUICKPAY_PAYMENT_SUCCESSFUL_CONTENT')}
                        </Typography>
                        <CheckCircle color={theme.palette.primary.main} className={classes.img} />
                        <Divider />
                    </Box>
                )}
                {!!order && !order?.isOpen && (
                    <>
                        {!isGuest && isOrderEmailNotificationsEnabled && (
                            <SummarySendReceiptSection
                                orderId={order?.id}
                                title={t('PAT_QUICKPAY_RESEND_RECEIPT_TITLE')}
                                footer={t('PAT_QUICKPAY_RESEND_RECEIPT_FOOTER')}
                                sentMessage={t('PAT_QUICKPAY_RESEND_RECEIPT_SENT')}
                            />
                        )}
                        {isGuest && isPhoneAuth && isOrderEmailNotificationsEnabled && (
                            <SummarySendReceiptSection
                                orderId={order?.id}
                                title={t('PAT_QUICKPAY_PHONE_GUEST_EMAIL_RECEIPT_TITLE')}
                                footer={t('PAT_QUICKPAY_PHONE_GUEST_EMAIL_RECEIPT_FOOTER')}
                                sentMessage={t('PAT_QUICKPAY_PHONE_GUEST_EMAIL_RECEIPT_SENT')}
                            />
                        )}
                        {isGuest && (
                            <SummaryGuestConversionSection
                                orderId={order?.id}
                                onAuthComplete={fetchOrder}
                                title={t('PAT_QUICKPAY_GUEST_CONVERSION_TITLE')}
                                subtitle={t('PAT_QUICKPAY_GUEST_CONVERSION_SUBTITLE')}
                                footer={t('PAT_QUICKPAY_GUEST_CONVERSION_FOOTER')}
                            />
                        )}
                        <Box px={2}>
                            <Divider />
                        </Box>
                    </>
                )}
                {reviewsEnabled && !reviewLoading && !!order && (
                    <Box p={2}>
                        <ReviewPrompt order={order} review={review} showSuccessCheckmark={false} />
                    </Box>
                )}
                {!!order && scenario === OrderScenario.DELIVERY && (
                    <Box>
                        <DeliverySuccessBox
                            due={due}
                            updatedAt={updatedAt}
                            statusString={statusString}
                            stage={deliveryStage}
                        />
                    </Box>
                )}
                {showPin && (
                    <Box p={2} pt={0} pb={1}>
                        <Divider />
                        <DeliveryPinBox pin={pin} />
                    </Box>
                )}
                {!loading && error === ErrorType.GENERIC && <PayError errorMessage={errorMessage} />}
                {!loading && (error === ErrorType.NOT_FOUND || !order) && (
                    <PayError errorMessage={errorMessage} />
                )}
                {!!order && (
                    <Box px={2}>
                        <Divider />
                        <PayItemsHeader
                            variant={showDelivery ? 'small' : 'default'}
                            paid
                            total={paid.otherPayments}
                        />
                    </Box>
                )}
                {!!order && !!order?.splitNumber && (
                    <PaySummaryPayments
                        payments={order?.payments}
                        users={order?.users}
                        balance={order?.total?.balance}
                    />
                )}
                {!!order && <PaySummaryItems order={order} collapseItems={!!order?.splitNumber || isGuest} />}
                {!!order && !!order?.isOpen && (
                    <PaySummaryReturn
                        checkId={order?.checkId}
                        balance={order?.total?.balance}
                        createdAt={order?.createdAt}
                        tableNumber={order?.deliveryLocation}
                    />
                )}
                {!!trackMyOrderLink && (
                    <Box height="50px" width="100%">
                        <Box position="fixed" bottom={0} left={0} right={0} p={1}>
                            <a href={trackMyOrderLink} target="_blank" rel="noreferrer">
                                <ButtonV2 color="primary">{t('DELIVERY_TRACK_ORDER')}</ButtonV2>
                            </a>
                        </Box>
                    </Box>
                )}
            </Paper>
        </StyledBox>
    );
};
